import { Box, IconButton, paletteColors, Tooltip, Typography } from '@evaneos/b2b-ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import CallEndOutlinedIcon from '@evaneos/b2b-ui/icons/CallEndOutlined';
import CircleIcon from '@evaneos/b2b-ui/icons/Circle';

import { MicOff } from '../../../../design-system/Icons/MicOff';
import { MicOn } from '../../../../design-system/Icons/MicOn';

import {
    pauseRecordingApi,
    resumeRecordingApi,
    startRecordingApi,
} from '../../../../resources/phoning/api';

import { DossierId } from '../../../../resources/dossier/types';
import { logPhoneCallError } from '../../../../resources/phoning/logger';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';
import { usePhoneCall } from '../../../../shared/providers/PhoneCallContext';

import { useRecordingDisplayActions } from './hook/useRecordingDisplayActions';
import { RecordingInformationModal } from './components/RecordingInformationModal';
import { RecordingButton } from './components/RecordingButton';
import { ActiveCallWidgetWrapper } from './components/ActiveCallWidgetWrapper';
import { Timer } from './components/Timer';

type RecordingStatus = 'idle' | 'recording' | 'paused';
type ConnectionStatus = 'connected' | 'reconnecting';

type ActiveCallWidgetProps = {
    requesterName: string;
    dossierId: DossierId;
};

/* eslint-disable max-lines-per-function -- active call widget with timer, mute and quality tracking logic */
export const ActiveCallWidget = ({ requesterName, dossierId }: ActiveCallWidgetProps) => {
    const { formatMessage } = useIntl();
    const { trackEvent } = useTrackEventAmplitude();

    const { call, state, hangUp } = usePhoneCall();

    const [recordingStatus, setRecordingStatus] = useState<RecordingStatus>('idle');
    const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connected');
    const [isMuted, setIsMuted] = useState(false);

    const isRecording = recordingStatus !== 'idle';
    const isRecordingPaused = recordingStatus === 'paused';
    const isReconnecting = connectionStatus === 'reconnecting';

    const trackedTripProperties = useGetTrackedTripProperties(dossierId);
    const {
        showRecordConfirmModal,
        shouldShowPulse,
        onConfirmRecording,
        onRejectRecording,
        onButtonHover,
    } = useRecordingDisplayActions({ status: state.status, isRecording });

    const callEndedEventSent = useRef(false);

    const handleHangUp = useCallback(() => {
        if (!callEndedEventSent.current) {
            trackEvent((ampli, defaultProperties) => {
                ampli.travelerCallEnded({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
            callEndedEventSent.current = true;
        }

        hangUp();
    }, [trackEvent, trackedTripProperties, hangUp]);

    const startRecording = useCallback(
        async (source: 'widget' | 'modale') => {
            if (!call || !state.phoneCallId) return;
            try {
                await startRecordingApi(state.phoneCallId);
                setRecordingStatus('recording');
                trackEvent((ampli, defaultProperties) => {
                    ampli.recordingStarted({
                        ...defaultProperties,
                        ...trackedTripProperties,
                        call_id: state.phoneCallId!.toString(),
                        recording_source: source,
                    });
                });
            } catch (error) {
                logPhoneCallError('Recording start failed', error, {
                    dossierId,
                    phoneCallId: state.phoneCallId,
                });
            }
        },
        [call, state.phoneCallId, trackEvent, trackedTripProperties, dossierId],
    );

    const pauseRecording = useCallback(async () => {
        const { phoneCallId } = state;
        if (!phoneCallId) return;

        try {
            await pauseRecordingApi(phoneCallId);
            setRecordingStatus('paused');
            trackEvent((ampli, defaultProperties) => {
                ampli.recordingPaused({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    call_id: phoneCallId.toString(),
                });
            });
        } catch (error) {
            logPhoneCallError('Recording pause failed', error, {
                dossierId,
                phoneCallId,
            });
        }
    }, [state, trackEvent, trackedTripProperties, dossierId]);

    const resumeRecording = useCallback(async () => {
        const { phoneCallId } = state;
        if (!phoneCallId) return;

        try {
            await resumeRecordingApi(phoneCallId);
            setRecordingStatus('recording');
            trackEvent((ampli, defaultProperties) => {
                ampli.recordingStarted({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    call_id: phoneCallId.toString(),
                    recording_source: 'widget',
                });
            });
        } catch (error) {
            logPhoneCallError('Recording resume failed', error, {
                dossierId,
                phoneCallId,
            });
        }
    }, [state, trackEvent, trackedTripProperties, dossierId]);

    const handleCloseModal = useCallback(() => {
        onRejectRecording();
    }, [onRejectRecording]);

    const handleConfirmRecordingFromModal = useCallback(() => {
        onConfirmRecording();
        void startRecording('modale');
    }, [startRecording, onConfirmRecording]);

    const handleConfirmRecordingFromWidget = useCallback(() => {
        onConfirmRecording();
        void startRecording('widget');
    }, [startRecording, onConfirmRecording]);

    const handleMute = useCallback(() => {
        if (!call) return;

        call.mute(!call.isMuted());
        setIsMuted(!isMuted);
    }, [call, isMuted]);

    // Handle reconnecting events - only during active calls
    useEffect(() => {
        if (call && state.status === 'active') {
            const handleReconnecting = () => setConnectionStatus('reconnecting');
            const handleReconnected = () => setConnectionStatus('connected');

            call.on('reconnecting', handleReconnecting);
            call.on('reconnected', handleReconnected);

            return () => {
                call.off('reconnecting', handleReconnecting);
                call.off('reconnected', handleReconnected);
            };
        }
    }, [call, state.status]);

    // eslint-disable-next-line sonarjs/function-return-type -- returns different JSX types depending on call state
    const renderCallTimer = () => {
        if (state.status === 'active') {
            return <Timer isReconnecting={isReconnecting} />;
        }

        return formatMessage({ id: 'phoning.calling' });
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                bottom: 90,
                left: { xs: 8, sm: 'unset' },
                right: { xs: 8, sm: 16 },
                width: { xs: 'auto', sm: 512 },
                zIndex: (theme) => theme.zIndex.modal,
            }}
        >
            {/* Recording modal  */}
            <RecordingInformationModal
                showRecordConfirmModal={showRecordConfirmModal}
                onConfirmRecording={handleConfirmRecordingFromModal}
                onCloseRecordingModal={handleCloseModal}
            />
            {/* Call Control Bar */}
            <ActiveCallWidgetWrapper>
                <Box
                    height="100%"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        gap: { xs: '8px', md: '16px' },
                        px: { xs: '8px', md: '0px' },
                    }}
                >
                    <Box>
                        <Typography color={paletteColors.neutral[0]} fontWeight="700">
                            {requesterName}
                        </Typography>
                        <Typography
                            color={
                                isRecording && !isRecordingPaused
                                    ? paletteColors.red[600]
                                    : paletteColors.neutral[0]
                            }
                            variant="body2"
                            sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            {isRecording && (
                                <>
                                    <CircleIcon
                                        sx={{
                                            color: isRecordingPaused
                                                ? paletteColors.neutral[600]
                                                : paletteColors.red[600],
                                            width: '8px',
                                            height: '8px',
                                        }}
                                    />
                                    <Typography
                                        component="span"
                                        color={
                                            isRecordingPaused
                                                ? paletteColors.neutral[600]
                                                : paletteColors.red[600]
                                        }
                                    >
                                        {isRecordingPaused
                                            ? formatMessage({
                                                  id: 'experiment.better_business.phoning.rec_paused',
                                              })
                                            : formatMessage({
                                                  id: 'experiment.better_business.phoning.rec',
                                              })}
                                    </Typography>
                                    <Typography
                                        component="span"
                                        color={
                                            isRecordingPaused
                                                ? paletteColors.neutral[600]
                                                : paletteColors.red[600]
                                        }
                                    >
                                        •
                                    </Typography>
                                </>
                            )}
                            {renderCallTimer()}
                        </Typography>
                    </Box>

                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{
                            gap: { xs: 0.5, md: 1 },
                            flexShrink: 0,
                        }}
                    >
                        <RecordingButton
                            status={state.status}
                            isRecording={isRecording}
                            isRecordingPaused={isRecordingPaused}
                            resumeRecording={resumeRecording}
                            pauseRecording={pauseRecording}
                            showRecordConfirmModal={showRecordConfirmModal}
                            shouldShowPulse={shouldShowPulse}
                            onButtonHover={onButtonHover}
                            handleConfirmRecording={handleConfirmRecordingFromWidget}
                        />
                        <Tooltip
                            placement="top"
                            disableInteractive
                            title={formatMessage({
                                id: isMuted ? 'phoning.unmute' : 'phoning.mute',
                            })}
                        >
                            <span>
                                <IconButton
                                    onClick={handleMute}
                                    data-testid="phoning-microphone-button"
                                    disabled={state.status !== 'active'}
                                    sx={{
                                        backgroundColor: paletteColors.neutral[100],
                                        borderRadius: '4px',
                                        color: paletteColors.neutral[700],
                                        height: '40px',
                                        width: '40px',
                                        border: '1px solid transparent',
                                        '&:hover': {
                                            backgroundColor: paletteColors.green[100],
                                            borderColor: paletteColors.green[600],
                                            color: paletteColors.green[700],
                                        },
                                        '&:disabled': {
                                            backgroundColor: paletteColors.neutral[100],
                                            color: paletteColors.neutral[700],
                                            opacity: 0.5,
                                        },
                                    }}
                                >
                                    {isMuted ? (
                                        <MicOn
                                            color="var(--ink)"
                                            data-testid="phoning-microphone-on"
                                            width={24}
                                            height={24}
                                        />
                                    ) : (
                                        <MicOff
                                            color="var(--ink)"
                                            data-testid="phoning-microphone-off"
                                            width={24}
                                            height={24}
                                        />
                                    )}
                                </IconButton>
                            </span>
                        </Tooltip>

                        <IconButton
                            onClick={handleHangUp}
                            color="error"
                            data-testid="phoning-hangup-button"
                            disabled={state.status !== 'active'}
                            sx={{
                                backgroundColor: paletteColors.red[600],
                                borderRadius: '4px',
                                color: paletteColors.neutral[0],
                                height: '40px',
                                width: '40px',
                                '&:hover': {
                                    backgroundColor: paletteColors.red[700],
                                },
                                '&:disabled': {
                                    backgroundColor: paletteColors.red[600],
                                    color: paletteColors.neutral[0],
                                    opacity: 0.5,
                                },
                            }}
                        >
                            <CallEndOutlinedIcon />
                        </IconButton>
                    </Box>
                </Box>
            </ActiveCallWidgetWrapper>
        </Box>
    );
};
/* eslint-enable max-lines-per-function */
